import { SupplierInfo } from 'types';
import { OrganizationInfo } from './OrganizationInfo/OrganizationInfo';
import { OrganizationIconUpload } from './OrganizationIconUpload/OrganizationIconUpload.';
import styles from './OrganizationGeneral.module.scss';
import * as yup from 'yup';
import { FormProvider, FormSubmitButton } from 'components/form';
import { Save } from '@mui/icons-material';
import { updateSupplierInfo } from 'lib/firebase';
import React, { SetStateAction, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useAppDispatch } from 'store';
import { activateSnackbar } from 'store/features/ui';

export type OrganizationInfoInput = {
    name: string;
    address: string;
    email: string;
    phone?: string;
    website?: string;
};

const organizationInfoSchema = yup.object<{
    [k in keyof OrganizationInfoInput]: yup.AnySchema;
}>({
    name: yup.string().required(),
    address: yup.string().required(),
    email: yup.string().required(),
    phone: yup.string(),
    website: yup.string(),
});

export interface OrganizationGeneralProps {
    supplierInfo: SupplierInfo;
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
    setSupplierInfo: React.Dispatch<SetStateAction<SupplierInfo | undefined>>;
}

export const OrganizationGeneral = ({
    supplierInfo,
    viewAsSupplier,
    setSupplierInfo,
}: OrganizationGeneralProps) => {
    const dispatch = useAppDispatch();

    const defaultOrganizationInfoValues: OrganizationInfoInput = {
        name: supplierInfo.name ?? '',
        address: supplierInfo.address ?? '',
        email: supplierInfo.email ?? '',
        phone: supplierInfo.phone ?? '',
        website: supplierInfo.website ?? '',
    };

    const [isSaving, setIsSaving] = useState(false);

    const onError = (error: FieldValues) => {
        setIsSaving(false);
        const messages = Object.keys(error).map((key) => error[key]?.message);
        dispatch(
            activateSnackbar({
                message: JSON.stringify(messages),
                severity: 'error',
            })
        );
    };

    const onSave = (infoInput: OrganizationInfoInput) => {
        setIsSaving(true);

        updateSupplierInfo(supplierInfo.supplierId, {
            ...supplierInfo,
            ...infoInput,
        })
            .then(() => {
                setSupplierInfo({
                    ...supplierInfo,
                    ...infoInput,
                });
                setIsSaving(false);
                dispatch(
                    activateSnackbar({
                        message: 'Saved Successfully',
                        severity: 'success',
                    })
                );
            })
            .catch(() => {
                setIsSaving(false);
            });
    };

    return (
        <div data-testid="OrganizationGeneral">
            <FormProvider
                defaultValues={defaultOrganizationInfoValues}
                validationSchema={organizationInfoSchema}
            >
                <div className={styles.cardRow}>
                    <div className={styles.cardCol}>
                        <OrganizationInfo />
                    </div>
                    <div className={styles.cardCol}>
                        <OrganizationIconUpload
                            viewAsSupplier={viewAsSupplier}
                        />
                    </div>
                </div>

                <div className={styles.saveButtonContainer}>
                    <FormSubmitButton
                        onValidHandler={(infoInput: OrganizationInfoInput) => {
                            onSave(infoInput);
                        }}
                        onErrorHandler={onError}
                        buttonLabel="Save"
                        startIcon={<Save />}
                        isLoading={isSaving}
                    />
                </div>
            </FormProvider>
        </div>
    );
};
